import { useFormik } from "formik";
import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { Calendar, momentLocalizer, Views } from "react-big-calendar";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { amPmTimeFormat } from "../../helpers";
import useApi from "../../useApi";
import "./index.css";

const initialValues = {
  location: null,
  bookingDate: moment().format("YYYY-MM-DD"),
};
const BigCalendar = ({ data, onChange, onReschedule, onNewBooking }) => {
  const navigate = useNavigate();
  const { getBookingsAdmin } = useApi();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);

  const formik = useFormik({
    initialValues,
  });

  const {
    touched,
    errors,
    values,
    handleSubmit,
    setFieldValue,
    setFieldTouched,
  } = formik;

  const getBookingsAdminApi = async (date, locationId, courtOptions) => {
    setLoading(true);

    let courtLength = courtOptions?.length || 10;

    const response = await getBookingsAdmin({
      bookingDateStart: date,
      bookingDateEnd: date,
      pageNumber: 1,
      pageSize: 50 * courtLength,
      locationId: locationId,
      bookingStatusId: 1,
      bookingStatusIds: [1,3],
    });

    if (response?.data?.headers?.error === 200) {
      const events = response?.data?.body?.items.map((item) => {
        const title = `${
          item?.bookingPlayers?.find((player) => player.isPrimary === true)
            ?.playerFullName || "--"
        }`;
        const start = moment(
          `${item?.bookingDate}T${item?.bookingStartTime}`
        ).toDate();
        let end = moment(`${item?.bookingDate}T${item?.bookingEndTime}`);

        if (item?.bookingEndTime === "00:00:00") {
          // Add 1 minute after midnight of next day
          end = moment(item?.bookingDate).endOf("day");
        }

        end = end.toDate();

        return {
          title,
          start,
          end,
          resourceId: item?.courtId,
          booking: item,
        };
      });

      setEvents(events);
    }
    if (response?.data?.headers?.error !== 200) {
      toast.error(response?.data?.headers?.message);
    }
  };

  useEffect(() => {
    if (data?.location && data?.bookingDate && data?.courtOptions?.length) {
      getBookingsAdminApi(
        moment(data?.bookingDate)?.format("YYYY-MM-DD"),
        data?.location,
        data?.courtOptions
      );
    }
  }, [data?.bookingDate, data?.location, data?.courtOptions]);

  const handleChangeWithTouch = (field) => (value) => {
    setFieldValue(field, value);
    setFieldTouched(field, true, false); // Mark field as touched manually
  };

  const CustomEvent = ({ event }) => {
    return (
      <div style={{ display: "flex", flexDirection: "column" }}>
        <div>{event.title}</div>
        <div>
          {event?.booking?.bookingNo?.split("-")?.slice(1).join("-") || "--"}
        </div>
        <div>
          {amPmTimeFormat(event?.booking?.bookingStartTime)} -{" "}
          {amPmTimeFormat(event?.booking?.bookingEndTime)}
        </div>
      </div>
    );
  };
  const hasOverlap = (start, end, resourceId) => {
    return events.some((event) => {
      return (
        event.resourceId === resourceId &&
        start < event.end &&
        end > event.start
      );
    });
  };

  const isBookingContinuouslyCovered = (
    bookingStart,
    bookingEnd,
    matchedTimings
  ) => {
    let cursor = bookingStart.clone();

    for (const timing of matchedTimings) {
      // Skip ranges that end before cursor      
      if (timing.timingEnd.isSameOrBefore(cursor, "minute")) continue;

      // If there's a gap between the cursor and the current timing
      if (timing.timingStart.isAfter(cursor, "minute")) {
        return false; // Gap found — booking not fully covered
      }

      // Extend cursor if this timing overlaps or continues from cursor
      if (timing.timingStart.isSameOrBefore(cursor, "minute")) {
        cursor = moment.max(cursor, timing.timingEnd);

        // If we've covered the whole booking, return true
        if (cursor.isSameOrAfter(bookingEnd, "minute")) {
          return true;
        }
      }
    }

    // If we reach here, bookingEnd wasn't fully covered
    return false;
  };

  const handleSelectSlot = (slotInfo) => {
    const { start, end, resourceId } = slotInfo;

    // Check if the selected court is closed
    const selectedCourt = data?.courtOptions?.find(
      (court) => court.value === resourceId
    );
    if (selectedCourt?.courtStatusId === 2) {
      toast.warning("This court is currently closed and cannot be selected");
      return;
    }

    const isEventSlot = hasOverlap(start, end, resourceId);

    if (!isEventSlot) {
      const date = moment(data?.bookingDate);
      const bookingDay = moment(data?.bookingDate).day();

      const bookingStart = moment(start);
      let bookingEnd = moment(end);

      if (bookingEnd.isSame(date.clone().endOf("day"), "minute")) {
        bookingEnd = date.clone().startOf("day").add(1, "day");
      }

      const court = data?.locationData
        ?.find((location) => location?.locationId == data?.location)
        ?.courts?.find((court) => court?.courtId == resourceId);

      if (court) {
        const courtSlotDuration = court?.courtSlotDuration;
        const matchedTimings = court?.courtTimings
          .filter((ct) => ct.courtDayOfWeek === bookingDay)
          .map((ct) => {
            const timingStart = date
              .clone()
              .startOf("day")
              .add(moment.duration(ct.courtStartTime));
            let timingEnd = date
              .clone()
              .startOf("day")
              .add(moment.duration(ct.courtEndTime));

            if (
              ct.courtEndTime === "00:00:00" ||
              ct.courtEndTime === "24:00:00"
            ) {
              timingEnd = date.clone().startOf("day").add(1, "day");
            }

            return { timingStart, timingEnd, courtCharges: ct.courtCharges };
          });

        const coverCheck = isBookingContinuouslyCovered(
          bookingStart,
          bookingEnd,
          matchedTimings
        );

        let totalCourtCharges = 0;

        matchedTimings.forEach((ct) => {
          const overlapStart = moment.max(bookingStart, ct.timingStart);
          const overlapEnd = moment.min(bookingEnd, ct.timingEnd);

          if (overlapStart.isBefore(overlapEnd)) {
            const minutes = overlapEnd.diff(overlapStart, "minutes");
            totalCourtCharges += Math.ceil(
              (minutes / courtSlotDuration) * ct.courtCharges
            );
          }
        });

        // Handle new booking for empty slot
        const slotData = {
          bookingDate: data?.bookingDate,
          courtId: resourceId,
          locationId: data?.location,
          startTime: bookingStart.format("HH:mm"),
          endTime: bookingEnd.format("HH:mm"),
          courtCharges: totalCourtCharges,
          bookingType: null,
        };

        if (!coverCheck) {
          slotData.bookingType = 9;
        }

        onNewBooking && onNewBooking(slotData);
      }
    }
  };

  const { components, views, defaultView, localizer, eventStyleGetter, resourceStyleGetter } =
    useMemo(
      () => ({
        components: {
          event: CustomEvent,
        },
        views: [Views.DAY],
        defaultView: Views.DAY,
        localizer: momentLocalizer(moment),
        eventStyleGetter: (event) => {
          return {
            style: {
              width: "100%",
              height: "100%",
              minHeight: "40px",
              backgroundColor: event.booking.bookingStatusId == 1 ? "var(--primary-color)" : "var(--amber-color)",
              color: "white",
              fontWeight: "bold",
              borderRadius: "4px",
              padding: "8px 12px",
              fontSize: "14px",
              display: "flex",
              justifyContent: "center",
            },
          };
        },
        resourceStyleGetter: (resource) => {
          const isClosed = resource.courtStatusId === 2;
          return {
            style: {
              backgroundColor: isClosed ? "#f8f9fa" : "transparent",
              color: isClosed ? "#6c757d" : "inherit",
              cursor: isClosed ? "not-allowed" : "pointer",
              opacity: isClosed ? 0.7 : 1,
              position: "relative",
              "&::after": isClosed
                ? {
                    content: '"Closed"',
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    backgroundColor: "rgba(0,0,0,0.7)",
                    color: "white",
                    padding: "4px 8px",
                    borderRadius: "4px",
                    fontSize: "12px",
                    zIndex: 1,
                  }
                : {},
            },
          };
        },
      }),
      []
    );

  return (
    <div className="CalendarContainer">
      <Calendar
        localizer={localizer}
        components={components}
        events={events}
        eventPropGetter={eventStyleGetter}
        defaultView={defaultView}
        views={views}
        step={30}
        timeslots={1}
        date={data?.bookingDate}
        resources={data?.courtOptions}
        resourceIdAccessor="value"
        resourceTitleAccessor="label"
        resourcePropGetter={resourceStyleGetter}
        style={{ height: "100%" }}
        selectable={true}
        draggableAccessor={(event) => event.isDraggable == false}
        isDraggable={false}
        onSelectEvent={(event) => {
          navigate(`/bookings/${event.booking?.bookingId}`, {
            state: {
              bookingId: event.booking?.bookingId,
              fromCalendar: true,
            },
          });
        }}
        onSelectSlot={handleSelectSlot}
        onNavigate={(newDate) => {
          onChange(newDate);
        }}
      />
    </div>
  );
};

export default BigCalendar;
